/* eslint-disable camelcase */
/* eslint-disable spire/fenced-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Clock from "@insite/mobius/Icons/Clock";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { HasCoveoHooks, withCoveoHooks } from "@NA/Components/Coveo/Contexts/CoveoHooksContext";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

import ADIColors from "@NA/Utils/ADIColors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedQuery?: string;
    closeAutocomplete: () => void;
    extendedStyles?: SearchHistoryStyles;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCoveoHooks;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLinkWrapper?: InjectableCss;
    clearLink?: LinkPresentationProps;
}

export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        weight: "bold",
        css: css`
            color: ${ADIColors.darkBlack3}!important;
            font-size: 16px;
            font-style: normal;
            font-weight: bold;
            line-height: normal;
            float: left;
            margin: 0;
            padding-bottom: 10px;
            width: 100%;

            @media (min-width: 360px) and (max-width: 1079px) {
                padding-top: 0;
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;

            width: 100%;
            padding: 0px;
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 0 6px 0;
        `,
    },
    icon: {
        src: Clock,
        css: css`
            margin-right: 10px;
            display: none;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                color: ${getColor("text.main")} !important;
                font-weight: normal;
                font-size: 14px;
                line-height: normal !important;
                font-style: normal;
                cursor: pointer;
                &:hover {
                    text-decoration: underline;
                }
            `,
        },
        css: css`
            width: auto;
            overflow: hidden;
            margin: 0;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            background-color: lightgray;
        `,
    },
    clearLinkWrapper: {
        css: css`
            width: 100%;
            border-top: 1px solid ${ADIColors.gray22};
        `,
    },
    clearLink: {
        typographyProps: {
            css: css`
                width: 100%;
                margin-top: 10px;
                color: ${getColor("primary")};
                text-align: left;
                font-size: 14px;
                font-weight: 600;
            `,
        },
    },
};

const CoveoRecentQueries = ({ closeAutocomplete, extendedStyles, recentQueriesList, searchBox }: Props) => {
    const styles = mergeToNew(searchHistoryStyles, extendedStyles);

    if (!recentQueriesList.state || recentQueriesList.state?.queries.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.headerText}>{translate("Search Suggestions")}</Typography>
            {recentQueriesList.state?.queries.map((query: string, index: number) => (
                <StyledWrapper {...styles.itemWrapper} key={`keywordSuggestions_${index}`}>
                    <Link
                        {...styles.link}
                        onClick={() => {
                            searchBox.methods?.updateText(query);
                            searchBox.methods?.submit();
                            closeAutocomplete();
                        }}
                    >
                        {query}
                    </Link>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withCoveoHooks(CoveoRecentQueries));
